import { useState } from 'react';

declare global {
  interface Window {
    lintrk: ((action: string, data?: { conversion_id: number }) => void) & { q: unknown[][] };
  }
}

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    revenue: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d60ach83qj6k6vba3n80', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', phone: '', revenue: '', message: '' });

        // LinkedIn conversion tracking — fires on successful form submission
        if (typeof window.lintrk === 'function') {
          window.lintrk('track', { conversion_id: 9643433 });
        }
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contato" className="py-24 bg-feenex-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left Column - Info */}
          <div>
            <div className="inline-block px-4 py-2 bg-feenex-gold/10 rounded-full mb-6">
              <span className="text-sm font-semibold text-feenex-gold uppercase tracking-wider">Contato</span>
            </div>
            
            <h4 className="text-4xl lg:text-5xl font-bold text-feenex-blue mb-6">
              <a href="#contato" className="hover:text-feenex-gold transition-colors">
                Fale com Nossos Especialistas
              </a>
            </h4>
            
            <p className="text-lg text-gray-600 mb-12 leading-relaxed">
              Agende uma consultoria sem compromisso e descubra como a Feenex pode ajudar sua empresa a reduzir impostos de forma legal e segura.
            </p>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-feenex-blue/5 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-xl text-feenex-blue"></i>
                </div>
                <div>
                  <div className="font-semibold text-feenex-blue mb-1">E-mail</div>
                  <a href="mailto:comercial@feenex.com.br" className="text-gray-600 hover:text-feenex-gold transition-colors cursor-pointer">
                    comercial@feenex.com.br
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-feenex-blue/5 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-xl text-feenex-blue"></i>
                </div>
                <div>
                  <div className="font-semibold text-feenex-blue mb-1">Telefone</div>
                  <a href="tel:+551140634000" className="text-gray-600 hover:text-feenex-gold transition-colors cursor-pointer">
                    +55 (11) 94770-5303
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-feenex-blue/5 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-xl text-feenex-blue"></i>
                </div>
                <div>
                  <div className="font-semibold text-feenex-blue mb-1">Endereço</div>
                  <p className="text-gray-600">
                    Rua Natalino Poppi, 232<br />
                    Osasco - SP, 06030-170
                  </p>
                </div>
              </div>
            </div>

            {/* Trust Badge */}
            <div className="mt-12 p-6 bg-feenex-blue/5 rounded-lg border border-feenex-blue/10">
              <div className="flex items-center gap-3 mb-3">
                <i className="ri-shield-check-line text-2xl text-feenex-gold"></i>
                <span className="font-semibold text-feenex-blue">Atendimento Confidencial</span>
              </div>
              <p className="text-sm text-gray-600">
                Todas as informações compartilhadas são tratadas com absoluto sigilo e protegidas por acordo de confidencialidade.
              </p>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="bg-white rounded-lg shadow-lg p-8 border border-gray-100">
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-feenex-blue mb-2">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-feenex-gold text-sm"
                  placeholder="Seu nome"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-feenex-blue mb-2">
                  E-mail Corporativo *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-feenex-gold text-sm"
                  placeholder="seu@email.com.br"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-semibold text-feenex-blue mb-2">
                  Empresa *
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-feenex-gold text-sm"
                  placeholder="Nome da empresa"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-feenex-blue mb-2">
                  Telefone *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-feenex-gold text-sm"
                  placeholder="(11) 99999-9999"
                />
              </div>

              <div>
                <label htmlFor="revenue" className="block text-sm font-semibold text-feenex-blue mb-2">
                  Faturamento Anual
                </label>
                <select
                  id="revenue"
                  name="revenue"
                  value={formData.revenue}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-feenex-gold text-sm cursor-pointer"
                >
                  <option value="">Selecione uma faixa</option>
                  <option value="ate-5mi">Até R$ 5 milhões</option>
                  <option value="5mi-20mi">R$ 5 a 20 milhões</option>
                  <option value="20mi-50mi">R$ 20 a 50 milhões</option>
                  <option value="50mi-100mi">R$ 50 a 100 milhões</option>
                  <option value="acima-100mi">Acima de R$ 100 milhões</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-feenex-blue mb-2">
                  Mensagem
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-feenex-gold text-sm resize-none"
                  placeholder="Conte-nos sobre suas necessidades..."
                ></textarea>
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {formData.message.length}/500 caracteres
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-feenex-blue hover:bg-feenex-gold text-white hover:text-feenex-blue font-semibold rounded-md transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Enviando...' : 'Solicitar Conversa Gratuita'}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-sm text-green-800">
                    Mensagem enviada com sucesso! Entraremos em contato em breve.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800">
                    Erro ao enviar mensagem. Por favor, tente novamente.
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
